import React, { useState, useEffect } from 'react';

export default function DealerAccessPanel() {
  const [dealers,    setDealers]    = useState([]);
  const [logs,       setLogs]       = useState([]);
  const [orders,     setOrders]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [toast,      setToast]      = useState(null);
  const [activeTab,  setActiveTab]  = useState('dealers');
  const [showForm,   setShowForm]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modal,      setModal]      = useState(null); // { type: 'range' | 'today' }
  const [form, setForm] = useState({ dealer_id: '', full_name: '', email: '', mobile: '' });

  // ── SSO Sessions date range (2026-07-24) ──────────────────────────────────
  // "Total SSO Sessions" used to render every matching log row in one go --
  // fine when there were a couple hundred, but with 3000+ sessions now
  // counted (see SESSION_ACTIONS fix below) that modal froze the tab
  // rendering thousands of DOM rows synchronously. Replaced with a bounded
  // date-range view: defaults to today only, both pickers capped at today
  // (no future dates), so the modal only ever has to render one day's worth
  // of sessions unless the admin deliberately widens the range.
  const toLocalDateStr = (d) => {
    const yyyy = d.getFullYear();
    const mm   = String(d.getMonth() + 1).padStart(2, '0');
    const dd   = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const todayStr = () => toLocalDateStr(new Date());
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo,   setDateTo]   = useState(todayStr());
  const handleDateFromChange = (val) => {
    const clamped = val > todayStr() ? todayStr() : val;
    setDateFrom(clamped);
    if (clamped > dateTo) setDateTo(clamped);
  };
  const handleDateToChange = (val) => {
    const clamped = val > todayStr() ? todayStr() : val;
    setDateTo(clamped);
    if (clamped < dateFrom) setDateFrom(clamped);
  };

  const token   = localStorage.getItem('nb_admin_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [dRes, lRes, oRes] = await Promise.all([
        fetch('/api/admin/dealers',      { headers }),
        fetch('/api/admin/dealer-logs',  { headers }),
        fetch('/api/admin/orders?placed_by=DEALER', { headers }),
      ]);
      const dData = await dRes.json();
      const lData = await lRes.json();
      const oData = await oRes.json();
      if (dData.success) setDealers(dData.dealers);
      if (lData.success) setLogs(lData.logs);
      if (oData.orders)  setOrders(oData.orders);
    } catch { showToast('Failed to load data.', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const handleCreateDealer = async () => {
    if (!form.dealer_id || !form.full_name || !form.email)
      return showToast('Dealer ID, Name and Email are required.', 'error');
    setSubmitting(true);
    try {
      const res  = await fetch('/api/admin/dealers/create', { method: 'POST', headers, body: JSON.stringify(form) });
      const data = await res.json();
      if (data.success) {
        showToast(`Dealer ${form.dealer_id} created successfully!`);
        setForm({ dealer_id: '', full_name: '', email: '', mobile: '' });
        setShowForm(false);
        loadData();
      } else {
        showToast(data.error || 'Failed to create dealer.', 'error');
      }
    } catch { showToast('Failed to create dealer.', 'error'); }
    finally { setSubmitting(false); }
  };

  const handleToggle = async (dealerId, current) => {
    try {
      const res  = await fetch('/api/admin/dealers/toggle', { method: 'POST', headers, body: JSON.stringify({ dealer_id: dealerId, is_active: !current }) });
      const data = await res.json();
      if (data.success) { showToast(`Dealer ${dealerId} ${!current ? 'activated' : 'deactivated'}`); loadData(); }
    } catch { showToast('Failed to update.', 'error'); }
  };

  const fmtDT = (ts) => ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtT  = (ts) => ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';

  // ── Stats ──────────────────────────────────────────────────────────────────
  // Dealer sessions are logged by TWO different backend flows: the legacy
  // 'SSO_GENERATED' action (old "open client in a new tab via SSO link" flow,
  // from POST /api/dealer/sso/generate) and the current 'CLIENT_DATA_ACCESSED'
  // action (in-page client load that replaced it, from POST
  // /api/dealer/client-data -- see that route's own comment: "replaces SSO
  // new-tab flow"). This card was only counting SSO_GENERATED, so once
  // dealers moved to the newer in-page flow, Total SSO Sessions froze at
  // whatever the old flow had accumulated and Today's Access always showed 0
  // -- every session (and any orders placed within it) created since the
  // switch was invisible here, even though it's captured fine in dealer_logs.
  const SESSION_ACTIONS = ['SSO_GENERATED', 'CLIENT_DATA_ACCESSED'];
  const ssoLogs    = logs.filter(l => SESSION_ACTIONS.includes(l.action));
  // Sessions within the selected [dateFrom, dateTo] range (inclusive, both
  // local-date comparisons so this lines up with the <input type="date">
  // values, which are already local-date strings).
  const rangeLogs  = ssoLogs.filter(l => {
    const d = toLocalDateStr(new Date(l.created_at));
    return d >= dateFrom && d <= dateTo;
  });

  // ── Build drill-down data ──────────────────────────────────────────────────
  const buildDrillRows = (logList) => logList.map(log => {
    // Find sq-off orders placed by this dealer for this client around same time
    const dealerOrders = orders.filter(o =>
      o.dealer_id === log.dealer_id &&
      o.ucc       === log.ucc
    );
    return { ...log, dealer_orders: dealerOrders };
  });

  const drillData = modal
    ? buildDrillRows(rangeLogs)
    : [];
  // Extra safety net: even a single selected day could theoretically have a
  // huge number of rows. Cap what actually gets rendered as DOM (the header
  // count and footer counts below still reflect the TRUE total) so the tab
  // can never freeze regardless of the chosen range.
  const RANGE_ROW_CAP  = 500;
  const drillDataShown = drillData.slice(0, RANGE_ROW_CAP);
  const drillDataMore  = drillData.length - drillDataShown.length;

  const tabStyle = (tab) => ({
    padding: '9px 22px', cursor: 'pointer', fontSize: 12, fontWeight: 500,
    color: activeTab === tab ? '#1d4ed8' : '#64748b',
    borderBottom: `2px solid ${activeTab === tab ? '#2563eb' : 'transparent'}`,
    background: activeTab === tab ? '#f8faff' : 'transparent',
    transition: 'all 0.15s'
  });

  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 7,
    border: '0.5px solid #e2e8f0', background: '#f8fafc',
    color: '#0f172a', fontSize: 13, outline: 'none',
    boxSizing: 'border-box', fontFamily: "'DM Sans',-apple-system,sans-serif"
  };

  const labelStyle = {
    fontSize: 10, color: '#64748b', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: 0.4,
    display: 'block', marginBottom: 5
  };

  return (
    <div style={{ fontFamily: "'DM Sans',-apple-system,sans-serif" }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, background: toast.type === 'error' ? '#fff5f5' : '#f0fdf4', border: `0.5px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`, color: toast.type === 'error' ? '#dc2626' : '#15803d', padding: '10px 18px', borderRadius: 8, fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
          {toast.msg}
        </div>
      )}

      {/* ── Drill-Down Modal ── */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 900, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>

            {/* Modal Header */}
            <div style={{ padding: '18px 24px 14px', borderBottom: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>SSO Sessions</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  {dateFrom === dateTo ? dateFrom : `${dateFrom} to ${dateTo}`} · {drillData.length} session{drillData.length !== 1 ? 's' : ''} — Dealer ID · Client · Date/Time · Actions
                </div>
              </div>
              <button onClick={() => setModal(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, color: '#374151', cursor: 'pointer' }}>✕ Close</button>
            </div>

            {/* Modal Body */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {drillData.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No sessions found.</div>
              ) : (
                <>
                  {/* Table Header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '90px 100px 150px 160px 1fr', padding: '10px 20px', background: '#f8fafc', borderBottom: `0.5px solid ${'#e2e8f0'}`, position: 'sticky', top: 0 }}>
                    {['Dealer ID', 'Client UCC', 'Date', 'Time', 'Actions Taken'].map(h => (
                      <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
                    ))}
                  </div>

                  {drillDataMore > 0 && (
                    <div style={{ padding: '8px 20px', background: '#fffbeb', borderBottom: '0.5px solid #fde68a', fontSize: 11, color: '#b45309' }}>
                      Showing first {RANGE_ROW_CAP} of {drillData.length} sessions — narrow the date range to see the rest.
                    </div>
                  )}
                  {drillDataShown.map((row, i) => (
                    <div key={row.log_id} style={{ borderBottom: i < drillDataShown.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: i%2===0?('#fff'):('#fafafa') }}>
                      {/* Main row */}
                      <div style={{ display: 'grid', gridTemplateColumns: '90px 100px 150px 160px 1fr', padding: '12px 20px', alignItems: 'center' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>{row.dealer_id}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{row.ucc || '—'}</div>
                        <div style={{ fontSize: 11, color: '#374151' }}>
                          {new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                        <div style={{ fontSize: 11, color: '#374151' }}>
                          {new Date(row.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </div>
                        <div>
                          {row.dealer_orders.length === 0 ? (
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>SSO link generated — no orders placed</span>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {row.dealer_orders.map(ord => (
                                <span key={ord.order_id} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 6, background: ord.side === 'SELL' ? '#fef2f2' : '#f0fdf4', color: ord.side === 'SELL' ? '#dc2626' : '#15803d', border: `0.5px solid ${ord.side === 'SELL' ? '#fecaca' : '#bbf7d0'}`, fontWeight: 600 }}>
                                  {ord.side} {ord.quantity} {ord.symbol} @ MKT
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Orders sub-rows if any */}
                      {row.dealer_orders.length > 0 && (
                        <div style={{ margin: '0 20px 12px', background: '#f8fafc', border: '0.5px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 80px 100px 120px', padding: '8px 14px', background: '#f1f5f9', borderBottom: '0.5px solid #e2e8f0' }}>
                            {['Symbol', 'Exch', 'Seg', 'Side', 'Qty', 'Status', 'Order Time'].map(h => (
                              <div key={h} style={{ fontSize: 9, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
                            ))}
                          </div>
                          {row.dealer_orders.map((ord, oi) => (
                            <div key={ord.order_id} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 80px 100px 120px', padding: '8px 14px', alignItems: 'center', borderBottom: oi < row.dealer_orders.length - 1 ? '0.5px solid #f1f5f9' : 'none' }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{ord.symbol}</div>
                              <div style={{ fontSize: 11, color: '#374151' }}>{ord.exchange}</div>
                              <div style={{ fontSize: 11, color: '#374151' }}>{ord.segment}</div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: ord.side === 'SELL' ? '#dc2626' : '#15803d' }}>{ord.side}</div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{ord.quantity}</div>
                              <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6, background: ord.status === 'ORDER_TRADED' ? '#f0fdf4' : ord.status === 'FILE_GENERATED' ? '#fff7ed' : '#eff6ff', color: ord.status === 'ORDER_TRADED' ? '#15803d' : ord.status === 'FILE_GENERATED' ? '#c2410c' : '#1d4ed8', border: `0.5px solid ${ord.status === 'ORDER_TRADED' ? '#bbf7d0' : ord.status === 'FILE_GENERATED' ? '#fed7aa' : '#bfdbfe'}`, fontWeight: 600 }}>
                                {ord.status === 'ORDER_TRADED' ? 'Traded' : ord.status === 'FILE_GENERATED' ? 'File Gen' : 'Received'}
                              </span>
                              <div style={{ fontSize: 10, color: '#64748b' }}>{fmtT(ord.placed_at)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '12px 24px', borderTop: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#64748b' }}>{drillData.length} total sessions · {drillData.filter(r => r.dealer_orders.length > 0).length} with orders</span>
              <button onClick={() => setModal(null)} style={{ padding: '7px 20px', borderRadius: 7, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Stat Card + Date Range ── */}
      {/* Date range now lives on the page itself (not inside the modal) --
          it drives both the SSO Sessions count shown here AND whatever the
          drill-down modal displays when opened, so the admin sets the range
          once and both stay in sync. A dedicated Submit button opens the
          modal explicitly for the currently selected range. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'SSO Sessions', value: rangeLogs.length, color: '#7c3aed', bg: '#fdf4ff', type: 'range' },
        ].map(c => (
          <div
            key={c.label}
            onClick={() => setModal({ type: c.type })}
            style={{ width: 260, background: '#fff', border: `0.5px solid #e2e8f0`, borderRadius: 8, padding: '14px 16px', cursor: 'pointer', transition: 'all 0.15s', position: 'relative' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = c.color; e.currentTarget.style.boxShadow = `0 0 0 2px ${c.bg}`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{c.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: c.color, textDecoration: 'underline', textUnderlineOffset: 3 }}>{c.value}</div>
              <span style={{ fontSize: 10, color: c.color, background: c.bg, padding: '2px 7px', borderRadius: 4, fontWeight: 600 }}>View Details →</span>
            </div>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 8, padding: '10px 14px' }}>
          <div>
            <label style={labelStyle}>From</label>
            <input type="date" value={dateFrom} max={todayStr()}
              onChange={e => handleDateFromChange(e.target.value)}
              style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '0.5px solid #e2e8f0', color: '#0f172a', fontFamily: "'DM Sans',-apple-system,sans-serif" }} />
          </div>
          <div>
            <label style={labelStyle}>To</label>
            <input type="date" value={dateTo} max={todayStr()}
              onChange={e => handleDateToChange(e.target.value)}
              style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '0.5px solid #e2e8f0', color: '#0f172a', fontFamily: "'DM Sans',-apple-system,sans-serif" }} />
          </div>
          <button onClick={() => setModal({ type: 'range' })}
            style={{ alignSelf: 'flex-end', padding: '7px 18px', borderRadius: 6, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',-apple-system,sans-serif" }}>
            Submit
          </button>
        </div>
      </div>

      {/* ── Create Dealer Form ── */}
      {showForm && (
        <div style={{ background: '#fff', border: '0.5px solid #bfdbfe', borderRadius: 10, padding: '24px', marginBottom: 20, boxShadow: '0 4px 16px rgba(37,99,235,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Create New Dealer</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Dealer will receive OTP on their registered email to login</div>
            </div>
            <button onClick={() => { setShowForm(false); setForm({ dealer_id: '', full_name: '', email: '', mobile: '' }); }}
              style={{ background: 'transparent', border: '0.5px solid #e2e8f0', color: '#64748b', padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
              ✕ Cancel
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
            {[
              { key: 'dealer_id', label: 'Dealer ID *',    placeholder: 'e.g. NB02',           note: 'Format: NB01, NB02...',  transform: v => v.toUpperCase() },
              { key: 'full_name', label: 'Full Name *',    placeholder: 'Dealer full name',     note: '' },
              { key: 'email',     label: 'Email ID *',     placeholder: 'dealer@navia.co.in',   note: '', type: 'email' },
              { key: 'mobile',    label: 'Mobile Number',  placeholder: '10-digit mobile',      note: '', type: 'tel', maxLength: 10 },
            ].map(f => (
              <div key={f.key}>
                <label style={labelStyle}>{f.label}</label>
                <input
                  type={f.type || 'text'}
                  value={form[f.key]}
                  maxLength={f.maxLength}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: f.transform ? f.transform(e.target.value) : e.target.value }))}
                  placeholder={f.placeholder}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = '#1d4ed8'}
                  onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                />
                {f.note && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{f.note}</div>}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleCreateDealer} disabled={submitting}
              style={{ padding: '9px 24px', borderRadius: 7, border: 'none', background: submitting ? '#93c5fd' : '#1d4ed8', color: '#fff', fontSize: 13, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer' }}>
              {submitting ? 'Creating...' : '+ Create Dealer'}
            </button>
            <button onClick={() => setForm({ dealer_id: '', full_name: '', email: '', mobile: '' })}
              style={{ padding: '9px 20px', borderRadius: 7, border: '0.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, cursor: 'pointer' }}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* ── Main Panel ── */}
      <div style={{ background: '#fff', border: `0.5px solid ${'#e2e8f0'}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'flex', borderBottom: '0.5px solid #e2e8f0', alignItems: 'center', justifyContent: 'space-between', paddingRight: 16 }}>
          <div style={{ display: 'flex' }}>
            <div style={tabStyle('dealers')} onClick={() => setActiveTab('dealers')}>Dealer List</div>
            <div style={tabStyle('logs')}    onClick={() => setActiveTab('logs')}>Access Logs</div>
          </div>
          {activeTab === 'dealers' && (
            <button onClick={() => setShowForm(!showForm)}
              style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: showForm ? '#f1f5f9' : '#1d4ed8', color: showForm ? '#64748b' : '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
              {showForm ? '✕ Close Form' : '+ Add Dealer'}
            </button>
          )}
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
        ) : activeTab === 'dealers' ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 200px 130px 160px 160px 140px', padding: '10px 20px', background: '#f8fafc', borderBottom: `0.5px solid ${'#e2e8f0'}` }}>
              {['Dealer ID', 'Full Name', 'Email', 'Mobile', 'Created On', 'Last Login', 'Status'].map(h => (
                <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
              ))}
            </div>
            {dealers.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>👤</div>
                <div style={{ fontSize: 14, color: '#64748b' }}>No dealers yet.</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>Click "+ Add Dealer" to create your first dealer.</div>
              </div>
            ) : dealers.map((d, i) => (
              <div key={d.dealer_id} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 200px 130px 160px 160px 140px', padding: '13px 20px', alignItems: 'center', borderBottom: i < dealers.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: d.is_active ? '#fff' : '#fafafa' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8' }}>{d.dealer_id}</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}>{d.full_name}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{d.email}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{d.mobile || '—'}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{fmtDT(d.created_at)}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{d.last_login ? fmtDT(d.last_login) : 'Never'}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: d.is_active ? '#f0fdf4' : '#fff5f5', color: d.is_active ? '#15803d' : '#dc2626', border: `0.5px solid ${d.is_active ? '#bbf7d0' : '#fecaca'}` }}>
                    {d.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <button onClick={() => handleToggle(d.dealer_id, d.is_active)}
                    style={{ padding: '3px 10px', borderRadius: 5, border: '0.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 10, cursor: 'pointer' }}>
                    {d.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '100px 120px 130px 1fr 160px', padding: '10px 20px', background: '#f8fafc', borderBottom: `0.5px solid ${'#e2e8f0'}` }}>
              {['Dealer ID', 'UCC', 'Action', 'Details', 'Timestamp'].map(h => (
                <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
              ))}
            </div>
            {logs.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>No access logs yet.</div>
            ) : logs.map((log, i) => (
              <div key={log.log_id} style={{ display: 'grid', gridTemplateColumns: '100px 120px 130px 1fr 160px', padding: '12px 20px', alignItems: 'center', borderBottom: i < logs.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: i%2===0?('#fff'):('#fafafa') }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>{log.dealer_id}</div>
                <div style={{ fontSize: 12, color: '#0f172a' }}>{log.ucc || '—'}</div>
                <div><span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#eff6ff', color: '#1d4ed8', border: '0.5px solid #bfdbfe' }}>{log.action}</span></div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{log.details || '—'}</div>
                <div style={{ fontSize: 10, color: '#64748b' }}>{fmtT(log.created_at)}</div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}